import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import {
  ArrowRight,
  ArrowDown,
  Users,
  HeartHandshake,
  ClipboardCheck,
  RefreshCw,
  CheckCircle,
  Target,
  BarChart3,
  Building2,
  Handshake,
  Zap,
  Phone,
  UserPlus,
  BookOpen,
  MessageCircle,
  Shield,
  Layers,
  Award,
  Sparkles,
} from "lucide-react";
import SEO from "../components/SEO";
import AnimateOnScroll from "../components/AnimateOnScroll";

const CALENDLY_URL = "https://calendly.com/rnb-consulting-group/free-discovery-call";

const HERO_IMAGE = "https://customer-assets.emergentagent.com/job_7865eaef-d264-4504-a997-7bd20f6ab0e8/artifacts/52uzf50e_360_F_1544467222_3YbZ53eP9rr5uXb7BmTo8Udad1fw91Oi.jpg";
const FRONT_DESK_IMAGE = "https://customer-assets.emergentagent.com/job_7865eaef-d264-4504-a997-7bd20f6ab0e8/artifacts/2y0k5kvj_front-desk-collections_OPTIMIZED.jpg";
const DATA_REVIEW_IMAGE = "https://customer-assets.emergentagent.com/job_7865eaef-d264-4504-a997-7bd20f6ab0e8/artifacts/w654fp2u_rs%3Dw_600%2Ch_300%2Ccg_true.webp";

const RcmPartnership = () => {
  const scrollToPartnership = () => {
    document.getElementById("partnership-options")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="RCM Partnership Practice Workflow Support"
        description="RNB Consulting Group partners with Revenue Cycle Management companies to help client practices understand how to apply billing processes within their day-to-day workflows."
      />

      {/* ===== SECTION 1: HERO ===== */}
      <section className="relative py-24 lg:py-32 bg-gradient-to-br from-rnb-navy via-rnb-navy to-rnb-blue overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-72 h-72 bg-rnb-teal rounded-full blur-3xl" />
          <div className="absolute bottom-10 right-20 w-96 h-96 bg-rnb-blue rounded-full blur-3xl" />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <AnimateOnScroll direction="left">
              <div>
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full mb-8 border border-white/20">
                  <HeartHandshake className="w-4 h-4 text-rnb-teal" />
                  <span className="text-sm text-white/90 font-medium">RCM Partnership Program</span>
                </div>
                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold text-white leading-tight mb-6" data-testid="hero-headline">
                  A Stronger Implementation Experience for{" "}
                  <span className="bg-gradient-to-r from-rnb-teal to-cyan-300 bg-clip-text text-transparent">
                    Your Clients
                  </span>
                </h1>
                <p className="text-lg text-white/80 leading-relaxed mb-4">
                  RNB Consulting Group partners with Revenue Cycle Management companies to help client practices understand how to apply your billing processes within their day-to-day workflows.
                </p>
                <p className="text-base text-white/60 leading-relaxed mb-8">
                  RCM teams do an excellent job implementing revenue cycle strategy. Where practices sometimes need additional support is translating those processes into their daily routines. We work directly with client practices to reinforce workflows, train staff, and help teams confidently operate within the systems and processes your organization has put in place.
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <Button
                    size="lg"
                    className="bg-gradient-to-r from-rnb-teal to-cyan-500 hover:from-rnb-teal/90 hover:to-cyan-500/90 text-white font-semibold px-8 py-6 text-base rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-0.5"
                    onClick={scrollToPartnership}
                    data-testid="hero-cta-primary"
                  >
                    Explore Partnership Options
                    <ArrowDown className="ml-2 h-5 w-5" />
                  </Button>
                  <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer">
                    <Button
                      size="lg"
                      variant="outline"
                      className="border-white/30 text-white hover:bg-white/10 px-8 py-6 text-base rounded-xl w-full"
                      data-testid="hero-cta-secondary"
                    >
                      <Phone className="mr-2 h-5 w-5" />
                      Start a Partnership Conversation
                    </Button>
                  </a>
                </div>
              </div>
            </AnimateOnScroll>
            <AnimateOnScroll direction="right">
              <div className="relative hidden lg:block">
                <div className="rounded-2xl overflow-hidden shadow-2xl border border-white/10">
                  <img
                    src={HERO_IMAGE}
                    alt="Healthcare consultant working with clinic staff at front desk"
                    className="w-full h-[500px] object-cover"
                  />
                </div>
              </div>
            </AnimateOnScroll>
            {/* Mobile hero image */}
            <div className="lg:hidden rounded-2xl overflow-hidden shadow-2xl">
              <img
                src={HERO_IMAGE}
                alt="Healthcare consultant working with clinic staff at front desk"
                className="w-full h-64 object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* ===== SECTION 2: THE PARTNERSHIP IDEA ===== */}
      <section className="py-20 lg:py-24 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimateOnScroll>
            <div className="text-center">
              <h2 className="text-3xl sm:text-4xl font-heading font-bold text-rnb-navy mb-10" data-testid="section2-headline">
                Extending the Value of Your Implementation Process
              </h2>
              <div className="space-y-6 text-left">
                <p className="text-base text-rnb-gray leading-relaxed">
                  When a new client begins working with an RCM company, there is often a lot of change happening at once new billing processes, updated workflows, and new expectations for documentation and intake.
                </p>
                <p className="text-base text-rnb-gray leading-relaxed">
                  Practices are usually eager to improve their revenue cycle, but they are also learning how to apply these new processes inside their everyday operations.
                </p>
                <p className="text-lg text-rnb-navy font-semibold italic">
                  That's where we collaborate.
                </p>
                <p className="text-base text-rnb-gray leading-relaxed">
                  RNB Consulting Group works directly with practices to help staff understand how the workflows your team introduces actually fit into their day-to-day responsibilities. By reinforcing these processes early, practices gain confidence faster and billing teams experience smoother implementation.
                </p>
              </div>
            </div>
          </AnimateOnScroll>
        </div>
      </section>

      {/* ===== SECTION 3: WHERE WE FIT IN ===== */}
      <section className="py-20 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <AnimateOnScroll direction="left">
              <div>
                <h2 className="text-3xl sm:text-4xl font-heading font-bold text-rnb-navy mb-6" data-testid="section3-headline">
                  Supporting the Practice Side of Implementation
                </h2>
                <p className="text-base text-rnb-gray leading-relaxed mb-4">
                  Think of us as an operational extension of your implementation process.
                </p>
                <p className="text-base text-rnb-gray leading-relaxed mb-8">
                  While your team focuses on designing and managing the revenue cycle strategy, we help practices learn how to operate within those systems in real time.
                </p>
                <p className="text-sm font-semibold text-rnb-navy mb-4">We commonly support areas such as:</p>
                <ul className="space-y-3 mb-8">
                  {[
                    "Front desk intake workflows",
                    "Insurance verification processes",
                    "Provider documentation habits",
                    "Communication between practice staff and billing teams",
                    "Staff training on new revenue cycle workflows",
                  ].map((item, i) => (
                    <li key={item.title || item.name || item.label || i} className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-rnb-teal shrink-0 mt-0.5" />
                      <span className="text-sm text-rnb-gray">{item}</span>
                    </li>
                  ))}
                </ul>
                <p className="text-base text-rnb-navy font-medium">
                  By helping practices strengthen these habits early, your implementation process becomes easier for teams to adopt and sustain.
                </p>
              </div>
            </AnimateOnScroll>
            <AnimateOnScroll direction="right">
              <div className="space-y-6">
                {/* Image */}
                <div className="rounded-2xl overflow-hidden shadow-lg">
                  <img
                    src={FRONT_DESK_IMAGE}
                    alt="Front desk staff working with patient intake workflows"
                    className="w-full h-64 object-cover"
                  />
                </div>
                {/* Diagram */}
                <div className="bg-gradient-to-br from-slate-50 to-white rounded-2xl p-6 border border-slate-200 shadow-sm">
                  <div className="flex items-center gap-3 flex-wrap justify-center">
                    <div className="bg-gradient-to-r from-rnb-navy to-rnb-blue rounded-xl px-5 py-3 text-white text-sm font-semibold text-center">
                      RCM Strategy
                    </div>
                    <span className="text-2xl font-bold text-rnb-teal">+</span>
                    <div className="bg-gradient-to-r from-rnb-blue to-blue-600 rounded-xl px-5 py-3 text-white text-sm font-semibold text-center">
                      Practice Workflow Training
                    </div>
                    <span className="text-2xl font-bold text-rnb-teal">=</span>
                    <div className="bg-gradient-to-r from-rnb-teal to-cyan-500 rounded-xl px-5 py-3 text-white text-sm font-semibold text-center">
                      Stronger Implementation
                    </div>
                  </div>
                </div>
              </div>
            </AnimateOnScroll>
          </div>
        </div>
      </section>

      {/* ===== SECTION 4: HOW COLLABORATION WORKS ===== */}
      <section className="py-20 lg:py-24 bg-gradient-to-br from-rnb-navy to-rnb-blue relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-96 h-96 bg-rnb-teal rounded-full blur-3xl" />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <AnimateOnScroll>
            <div className="text-center max-w-3xl mx-auto mb-16">
              <h2 className="text-3xl sm:text-4xl font-heading font-bold text-white mb-4" data-testid="section4-headline">
                What Working Together Often Looks Like
              </h2>
            </div>
          </AnimateOnScroll>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                step: "01",
                title: "Implementation Alignment",
                desc: "We review the workflows and processes your team is introducing to the practice so we understand how your system is intended to operate.",
                icon: ClipboardCheck,
              },
              {
                step: "02",
                title: "Practice Workflow Training",
                desc: "We work with practice staff to help them understand how these processes translate into their day-to-day responsibilities.",
                icon: BookOpen,
              },
              {
                step: "03",
                title: "Workflow Reinforcement",
                desc: "Through hands-on support and training, we help teams develop consistent habits that support your revenue cycle strategy.",
                icon: RefreshCw,
              },
              {
                step: "04",
                title: "Operational Confidence",
                desc: "Practices become more comfortable operating within the systems and processes your team has implemented.",
                icon: Award,
              },
            ].map((step, i) => (
              <AnimateOnScroll key={step.title || step.name || i} delay={i * 0.15}>
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20 hover:bg-white/15 transition-all duration-300 h-full flex flex-col">
                  <div className="flex items-center gap-3 mb-4">
                    <span className="text-3xl font-bold text-rnb-teal font-mono">{step.step}</span>
                    <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center">
                      <step.icon className="w-5 h-5 text-white" />
                    </div>
                  </div>
                  <h3 className="font-heading font-semibold text-white mb-3">{step.title}</h3>
                  <p className="text-sm text-white/70 leading-relaxed flex-1">{step.desc}</p>
                </div>
              </AnimateOnScroll>
            ))}
          </div>
        </div>
      </section>

      {/* ===== SECTION 5: PARTNERSHIP OPTIONS ===== */}
      <section id="partnership-options" className="py-20 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimateOnScroll>
            <div className="text-center max-w-3xl mx-auto mb-16">
              <h2 className="text-3xl sm:text-4xl font-heading font-bold text-rnb-navy mb-4" data-testid="section5-headline">
                Flexible Ways We Can Partner
              </h2>
            </div>
          </AnimateOnScroll>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                title: "Preferred Referral Partner",
                desc: "RCM companies may refer client practices to RNB Consulting Group when additional workflow support or staff training would help strengthen implementation success.",
                detail: "Practices work with us directly while your team continues focusing on the revenue cycle services you provide.",
                icon: Handshake,
                gradient: "from-rnb-navy to-rnb-blue",
              },
              {
                title: "White Label Collaboration",
                desc: "For organizations seeking a seamless experience, RNB Consulting Group can operate as a white-label partner.",
                detail: "In this model, our consultants support client practices as an extension of your team, helping reinforce the workflows and processes your organization introduces.",
                icon: Layers,
                gradient: "from-rnb-blue to-blue-600",
              },
              {
                title: "Implementation Support Add-On",
                desc: "Some RCM companies choose to offer RNB Consulting Group as an optional implementation enhancement for clients who would benefit from additional workflow training and operational reinforcement.",
                detail: "This allows your organization to provide a more concierge-style onboarding experience without expanding internal resources.",
                icon: Sparkles,
                gradient: "from-rnb-teal to-cyan-500",
              },
            ].map((option, i) => (
              <AnimateOnScroll key={option.title || option.name || i} delay={i * 0.15}>
                <Card className="h-full border-0 shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-1 overflow-hidden group">
                  <div className={`h-1.5 bg-gradient-to-r ${option.gradient}`} />
                  <CardContent className="p-7">
                    <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${option.gradient} flex items-center justify-center mb-6 group-hover:scale-110 transition-transform`}>
                      <option.icon className="w-7 h-7 text-white" />
                    </div>
                    <h3 className="font-heading font-bold text-rnb-navy text-lg mb-3">{option.title}</h3>
                    <p className="text-sm text-rnb-gray leading-relaxed mb-3">{option.desc}</p>
                    <p className="text-sm text-rnb-gray leading-relaxed">{option.detail}</p>
                  </CardContent>
                </Card>
              </AnimateOnScroll>
            ))}
          </div>
        </div>
      </section>

      {/* ===== SECTION 6: WHEN PARTNERS INVOLVE US ===== */}
      <section className="py-20 lg:py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimateOnScroll>
            <div className="text-center max-w-3xl mx-auto mb-16">
              <h2 className="text-3xl sm:text-4xl font-heading font-bold text-rnb-navy mb-4" data-testid="section6-headline">
                Situations Where This Collaboration Is Often Helpful
              </h2>
            </div>
          </AnimateOnScroll>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                icon: UserPlus,
                title: "New Client Onboarding",
                gradient: "from-rnb-navy to-rnb-blue",
              },
              {
                icon: RefreshCw,
                title: "Practices Adjusting to New Billing Workflows",
                gradient: "from-rnb-blue to-blue-600",
              },
              {
                icon: BookOpen,
                title: "Teams That Benefit From Additional Operational Training",
                gradient: "from-indigo-500 to-rnb-blue",
              },
              {
                icon: MessageCircle,
                title: "Practices Learning to Work More Closely With Their Billing Team",
                gradient: "from-rnb-teal to-cyan-500",
              },
            ].map((card, i) => (
              <AnimateOnScroll key={card.title || card.name || card.label || i} delay={i * 0.1}>
                <Card className="h-full border-0 shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-1 overflow-hidden group">
                  <div className={`h-1.5 bg-gradient-to-r ${card.gradient}`} />
                  <CardContent className="p-6 text-center">
                    <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${card.gradient} flex items-center justify-center mx-auto mb-5 group-hover:scale-110 transition-transform`}>
                      <card.icon className="w-7 h-7 text-white" />
                    </div>
                    <h3 className="font-heading font-semibold text-rnb-navy leading-relaxed">{card.title}</h3>
                  </CardContent>
                </Card>
              </AnimateOnScroll>
            ))}
          </div>
        </div>
      </section>

      {/* ===== SECTION 7: BENEFITS ===== */}
      <section className="py-20 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <AnimateOnScroll direction="left">
              <div>
                <h2 className="text-3xl sm:text-4xl font-heading font-bold text-rnb-navy mb-10" data-testid="section7-headline">
                  Why RCM Teams Value This Partnership
                </h2>
                <div className="space-y-6">
                  {[
                    {
                      icon: BarChart3,
                      title: "Stronger Implementation Experiences",
                      desc: "Stronger implementation experiences for client practices.",
                      color: "text-rnb-blue",
                      bg: "bg-blue-100",
                    },
                    {
                      icon: Users,
                      title: "Teams That Understand Your Workflows",
                      desc: "Teams that understand how to operate within your workflows.",
                      color: "text-rnb-teal",
                      bg: "bg-teal-100",
                    },
                    {
                      icon: HeartHandshake,
                      title: "Smoother Collaboration",
                      desc: "Smoother collaboration between practice staff and billing teams.",
                      color: "text-green-600",
                      bg: "bg-green-100",
                    },
                    {
                      icon: Building2,
                      title: "Expanded Client Support",
                      desc: "Expanded client support without expanding internal teams.",
                      color: "text-indigo-600",
                      bg: "bg-indigo-100",
                    },
                  ].map((benefit, i) => (
                    <div key={benefit.title || benefit.name || i} className="flex items-start gap-5">
                      <div className={`w-12 h-12 rounded-xl ${benefit.bg} flex items-center justify-center shrink-0`}>
                        <benefit.icon className={`w-6 h-6 ${benefit.color}`} />
                      </div>
                      <div>
                        <h3 className="font-heading font-semibold text-rnb-navy mb-1">{benefit.title}</h3>
                        <p className="text-sm text-rnb-gray leading-relaxed">{benefit.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </AnimateOnScroll>
            <AnimateOnScroll direction="right">
              <div className="rounded-2xl overflow-hidden shadow-lg">
                <img
                  src={DATA_REVIEW_IMAGE}
                  alt="Healthcare professional reviewing billing data and documentation"
                  className="w-full h-[420px] object-cover"
                />
              </div>
            </AnimateOnScroll>
          </div>
        </div>
      </section>

      {/* ===== SECTION 8: TEST THE PARTNERSHIP ===== */}
      <section className="py-20 lg:py-24 bg-gradient-to-br from-rnb-navy via-rnb-navy to-rnb-blue relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-10 right-10 w-80 h-80 bg-rnb-teal/15 rounded-full blur-3xl" />
          <div className="absolute bottom-10 left-20 w-64 h-64 bg-cyan-400/10 rounded-full blur-3xl" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-white/5 rounded-full blur-3xl" />
        </div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <AnimateOnScroll>
            <div className="text-center">
              <div className="inline-flex items-center gap-2 px-5 py-2.5 bg-rnb-teal/20 backdrop-blur-sm rounded-full mb-8 border border-rnb-teal/30">
                <Target className="w-5 h-5 text-rnb-teal" />
                <span className="text-sm text-white font-semibold tracking-wide">Get Started</span>
              </div>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold text-white mb-8 leading-tight" data-testid="pilot-headline">
                Test the Partnership With a<br className="hidden sm:block" /> Few Client Practices
              </h2>
              <div className="max-w-2xl mx-auto space-y-5 mb-12">
                <p className="text-lg text-white/85 leading-relaxed">
                  Not sure how this would fit into your implementation process?
                </p>
                <p className="text-base text-white/70 leading-relaxed">
                  A great place to start is by introducing RNB Consulting Group to a few client practices that would benefit from additional workflow support or operational training.
                </p>
                <p className="text-base text-white/70 leading-relaxed">
                  This allows your team to see how reinforcing practice workflows can strengthen the overall implementation experience and help practices feel more confident operating within your systems.
                </p>
              </div>
              <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer">
                <Button
                  size="lg"
                  className="bg-gradient-to-r from-rnb-teal to-cyan-400 hover:from-rnb-teal/90 hover:to-cyan-400/90 text-white font-bold px-12 py-7 text-lg rounded-xl shadow-[0_0_40px_rgba(20,150,127,0.3)] hover:shadow-[0_0_60px_rgba(20,150,127,0.5)] transition-all duration-300 hover:-translate-y-1"
                  data-testid="pilot-cta"
                >
                  Start a Partnership Conversation
                  <ArrowRight className="ml-3 h-5 w-5" />
                </Button>
              </a>
            </div>
          </AnimateOnScroll>
        </div>
      </section>

      {/* ===== SECTION 9: WHO THIS WORKS FOR ===== */}
      <section className="py-20 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimateOnScroll>
            <div className="text-center max-w-3xl mx-auto mb-16">
              <h2 className="text-3xl sm:text-4xl font-heading font-bold text-rnb-navy mb-4" data-testid="section9-headline">
                Ideal Partnership Fit
              </h2>
            </div>
          </AnimateOnScroll>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {[
              {
                title: "RCM companies onboarding independent medical practices.",
                icon: Building2,
                gradient: "from-rnb-blue to-blue-600",
              },
              {
                title: "Organizations looking to enhance their implementation experience.",
                icon: Shield,
                gradient: "from-rnb-teal to-cyan-500",
              },
              {
                title: "Teams that want to provide additional training and workflow support for clients.",
                icon: Handshake,
                gradient: "from-indigo-500 to-rnb-blue",
              },
            ].map((card, i) => (
              <AnimateOnScroll key={card.title || card.name || card.label || i} delay={i * 0.15}>
                <Card className="h-full border-0 shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-1 overflow-hidden text-center group">
                  <div className={`h-1.5 bg-gradient-to-r ${card.gradient}`} />
                  <CardContent className="p-8">
                    <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${card.gradient} flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform`}>
                      <card.icon className="w-8 h-8 text-white" />
                    </div>
                    <h3 className="font-heading font-semibold text-rnb-navy leading-relaxed">{card.title}</h3>
                  </CardContent>
                </Card>
              </AnimateOnScroll>
            ))}
          </div>
        </div>
      </section>

      {/* ===== SECTION 10: FINAL CTA ===== */}
      <section className="py-20 lg:py-24 bg-gradient-to-br from-rnb-navy via-rnb-navy to-rnb-blue relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-rnb-teal rounded-full blur-3xl" />
          <div className="absolute top-0 right-0 w-72 h-72 bg-cyan-400 rounded-full blur-3xl" />
        </div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative">
          <AnimateOnScroll>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold text-white mb-6" data-testid="final-cta-headline">
              Interested in Exploring a Partnership?
            </h2>
            <p className="text-lg text-white/80 mb-6 max-w-2xl mx-auto leading-relaxed">
              If your organization works with practices that may benefit from additional workflow training or operational reinforcement during implementation, we would be happy to explore how our teams could collaborate.
            </p>
            <p className="text-base text-white/60 mb-10 max-w-2xl mx-auto">
              Tell us about your implementation process and the types of practices you support, and we will follow up to discuss potential partnership options.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer">
                <Button
                  size="lg"
                  className="bg-gradient-to-r from-rnb-teal to-cyan-500 hover:from-rnb-teal/90 hover:to-cyan-500/90 text-white font-semibold px-10 py-6 text-base rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-0.5"
                  data-testid="final-cta-primary"
                >
                  Start a Partnership Conversation
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </a>
              <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer">
                <Button
                  size="lg"
                  variant="outline"
                  className="border-white/30 text-white hover:bg-white/10 px-10 py-6 text-base rounded-xl"
                  data-testid="final-cta-secondary"
                >
                  <Phone className="mr-2 h-5 w-5" />
                  Schedule a Call
                </Button>
              </a>
            </div>
          </AnimateOnScroll>
        </div>
      </section>

    </div>
  );
};

export default RcmPartnership;
